import { Link } from '@tanstack/react-router'
import { Factory, Package, Search } from 'lucide-react'
import { parseAsInteger, parseAsString, parseAsStringLiteral, useQueryStates } from 'nuqs'
import { ListFilter, ListFilters, ResultCount } from '@/components/list-filters'
import { SearchSelect } from '@/components/search-select'
import { StatusPill } from '@/components/status-pill'
import { Button } from '@/components/ui/button'
import { SearchInput } from '@/components/search-input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useUrlSearchText } from '@/hooks/use-url-search-text'
import { cn, parseUTCDate, resultCount } from '@/lib/utils'
import {
  READINESS_STATUS,
  READINESS_STEPS,
  type ReadinessStep,
  type StoreReadinessManufacturer,
  type StoreReadinessProduct,
  useStoreReadinessManufacturers,
  useStoreReadinessProducts,
} from '@/features/store-readiness/hooks'

const LINK = 'font-medium text-primary hover:underline'

const STATUS_OPTIONS = [
  { value: 'all', label: 'All states' },
  { value: 'stuck', label: 'Stuck' },
  { value: 'waiting', label: 'Waiting' },
  { value: 'in_progress', label: 'In progress' },
] as const

type StatusFilter = (typeof STATUS_OPTIONS)[number]['value']

function shortDay(value: string) {
  const date = parseUTCDate(value)
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }),
  })
}

function HeldBy({ step, then }: { step: ReadinessStep; then?: string[] }) {
  const status = READINESS_STATUS[step.status]
  return (
    <div className="min-w-0 space-y-0.5">
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-sm font-medium">{step.label}</span>
        <StatusPill tone={status.tone}>{status.label}</StatusPill>
      </p>
      {step.detail && <p className="text-sm text-muted-foreground">{step.detail}</p>}
      {then && then.length > 0 && (
        <p className="text-xs text-muted-foreground">Then {then.join(', ')}</p>
      )}
    </div>
  )
}

function ProductCell({ row }: { row: StoreReadinessProduct }) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <div className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-white">
        {row.image_url ? (
          <img src={row.image_url} alt="" loading="lazy" className="size-full object-contain p-0.5" />
        ) : (
          <Package className="size-4 text-muted-foreground/40" aria-hidden="true" />
        )}
      </div>
      <div className="min-w-0">
        <Link to="/products/$productId" params={{ productId: row.uuid }} className={cn(LINK, 'block truncate')}>
          {row.model}
        </Link>
        {row.title && (
          <p className="line-clamp-1 text-xs text-muted-foreground" title={row.title}>
            {row.title}
          </p>
        )}
      </div>
    </div>
  )
}

function ManufacturerLink({
  manufacturer,
  className,
}: {
  manufacturer: StoreReadinessProduct['manufacturer']
  className?: string
}) {
  return (
    <Link
      to="/manufacturers/$manuId"
      params={{ manuId: manufacturer.uuid }}
      className={cn('text-sm hover:text-primary hover:underline', className)}
    >
      {manufacturer.name}
    </Link>
  )
}

function ProductsTable({ rows }: { rows: StoreReadinessProduct[] }) {
  return (
    <>
      <div className="hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead>Manufacturer</TableHead>
              <TableHead>Held by</TableHead>
              <TableHead className="text-right">Added</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.uuid} className="align-top">
                <TableCell className="max-w-[340px] whitespace-normal">
                  <ProductCell row={row} />
                </TableCell>
                <TableCell className="whitespace-normal">
                  <ManufacturerLink manufacturer={row.manufacturer} />
                </TableCell>
                <TableCell className="max-w-[360px] whitespace-normal">
                  <HeldBy step={row.held_by} then={row.also_waiting} />
                </TableCell>
                <TableCell className="w-28 text-right text-sm tabular-nums text-muted-foreground">
                  {shortDay(row.added_at)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="space-y-2 lg:hidden">
        {rows.map((row) => (
          <div key={row.uuid} className="space-y-2 rounded-lg border bg-card p-3">
            <div className="flex items-start justify-between gap-3">
              <ProductCell row={row} />
              <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{shortDay(row.added_at)}</span>
            </div>
            <ManufacturerLink manufacturer={row.manufacturer} className="text-xs text-muted-foreground" />
            <HeldBy step={row.held_by} then={row.also_waiting} />
          </div>
        ))}
      </div>
    </>
  )
}

function HeldByCounts({
  row,
  onOpen,
}: {
  row: StoreReadinessManufacturer
  onOpen: (step?: string) => void
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {(row.held_by ?? []).map((count) => (
        <button
          key={count.key}
          type="button"
          onClick={() => onOpen(count.key)}
          className="cursor-pointer rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
        >
          <StatusPill tone={count.stuck > 0 ? 'danger' : 'progress'} className="gap-1 text-xs hover:brightness-95">
            {count.label}
            <span className="tabular-nums">{count.products}</span>
            {count.stuck > 0 && <span>stuck</span>}
          </StatusPill>
        </button>
      ))}
    </div>
  )
}

function ManufacturerName({ row }: { row: StoreReadinessManufacturer }) {
  return (
    <div className="min-w-0 space-y-0.5">
      <Link
        to="/manufacturers/$manuId"
        params={{ manuId: row.manufacturer.uuid }}
        className={cn(LINK, 'block')}
      >
        {row.manufacturer.name}
      </Link>
      {row.brand_page && (
        <p className="text-xs text-muted-foreground">
          Brand page: {row.brand_page.detail ?? READINESS_STATUS[row.brand_page.status].label}
        </p>
      )}
    </div>
  )
}

function WaitingCount({ row, onOpen }: { row: StoreReadinessManufacturer; onOpen: () => void }) {
  if (row.waiting === 0) return <span className="text-sm text-muted-foreground">None</span>
  return (
    <button type="button" onClick={onOpen} className={cn(LINK, 'shrink-0 cursor-pointer whitespace-nowrap text-sm tabular-nums')}>
      {row.waiting} {row.waiting === 1 ? 'product' : 'products'}
    </button>
  )
}

function ManufacturersTable({
  rows,
  onOpen,
}: {
  rows: StoreReadinessManufacturer[]
  onOpen: (manufacturerUuid: string, step?: string) => void
}) {
  return (
    <>
      <div className="hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Manufacturer</TableHead>
              <TableHead>Waiting</TableHead>
              <TableHead>Held by</TableHead>
              <TableHead className="text-right">Oldest</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.manufacturer.uuid} className="align-top">
                <TableCell className="max-w-[280px] whitespace-normal">
                  <ManufacturerName row={row} />
                </TableCell>
                <TableCell className="w-36">
                  <WaitingCount row={row} onOpen={() => onOpen(row.manufacturer.uuid)} />
                </TableCell>
                <TableCell className="whitespace-normal">
                  <HeldByCounts row={row} onOpen={(step) => onOpen(row.manufacturer.uuid, step)} />
                </TableCell>
                <TableCell className="w-32 text-right text-sm tabular-nums text-muted-foreground">
                  {row.oldest_added_at ? shortDay(row.oldest_added_at) : ''}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="space-y-2 lg:hidden">
        {rows.map((row) => (
          <div key={row.manufacturer.uuid} className="space-y-2 rounded-lg border bg-card p-3">
            <div className="flex items-start justify-between gap-3">
              <ManufacturerName row={row} />
              <WaitingCount row={row} onOpen={() => onOpen(row.manufacturer.uuid)} />
            </div>
            <HeldByCounts row={row} onOpen={(step) => onOpen(row.manufacturer.uuid, step)} />
          </div>
        ))}
      </div>
    </>
  )
}

function TableSkeleton() {
  return (
    <div className="space-y-2" role="status" aria-label="Loading">
      {Array.from({ length: 6 }).map((_, index) => (
        <Skeleton key={index} className="h-16 w-full" />
      ))}
    </div>
  )
}

/** What holds each product back from the store, by product and by manufacturer (USA-1131). */
export function StoreReadinessPage() {
  const [search, setSearch] = useUrlSearchText()
  const [filters, setFilters] = useQueryStates({
    view: parseAsStringLiteral(['products', 'manufacturers'] as const).withDefault('products'),
    step: parseAsString,
    status: parseAsStringLiteral(['all', 'stuck', 'waiting', 'in_progress'] as const).withDefault('all'),
    manufacturer: parseAsString,
    page: parseAsInteger.withDefault(1),
  })
  const { view, step, status, manufacturer, page } = filters
  const manufacturers = useStoreReadinessManufacturers()
  const products = useStoreReadinessProducts(
    {
      manufacturer_uuid: manufacturer ?? undefined,
      step: step ?? undefined,
      status: status === 'all' ? undefined : status,
      search: search || undefined,
      page,
      limit: 20,
    },
    view === 'products',
  )
  const query = search.trim().toLowerCase()
  const manufacturerRows = (manufacturers.data ?? []).filter(
    (row) => !query || row.manufacturer.name.toLowerCase().includes(query),
  )
  const openProducts = (manufacturerUuid: string, nextStep?: string) =>
    void setFilters({ view: 'products', manufacturer: manufacturerUuid, step: nextStep ?? null, status: 'all', page: 1 })
  const active = [step, status !== 'all' ? status : null, manufacturer].filter(Boolean).length
  const loading = view === 'products' ? products.isLoading : manufacturers.isLoading
  const searchBox = (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <SearchInput
        placeholder={view === 'products' ? 'Search model or title...' : 'Search manufacturers...'}
        aria-label="Search store readiness"
        value={search}
        onChange={(event) => {
          setSearch(event.target.value)
          void setFilters({ page: 1 })
        }}
        className="pl-9"
      />
    </div>
  )

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold tracking-tight">Store readiness</h1>
        <div
          role="group"
          aria-label="Store readiness view"
          className="inline-grid grid-cols-2 rounded-lg border bg-muted/40 p-1"
        >
          {(
            [
              { value: 'products', label: 'Products', Icon: Package },
              { value: 'manufacturers', label: 'Manufacturers', Icon: Factory },
            ] as const
          ).map(({ value, label, Icon }) => (
            <button
              key={value}
              type="button"
              aria-pressed={view === value}
              onClick={() => void setFilters({ view: value, page: 1 })}
              className={cn(
                'inline-flex min-h-8 cursor-pointer items-center justify-center gap-2 rounded-md px-3 text-xs font-medium text-muted-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
                view === value && 'bg-background text-foreground shadow-sm',
              )}
            >
              <Icon className="size-3.5" aria-hidden="true" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {view === 'manufacturers' ? (
        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-0 flex-1 basis-56 sm:max-w-md">{searchBox}</div>
          <ResultCount>
            {manufacturers.data ? resultCount(manufacturerRows.length, 'manufacturer') : undefined}
          </ResultCount>
        </div>
      ) : (
        <ListFilters
          count={resultCount(products.data?.total, 'product')}
          active={active}
          onClear={() => void setFilters({ step: null, status: null, manufacturer: null, page: 1 })}
          search={searchBox}
        >
          <ListFilter label="Held by">
            <SearchSelect
              ariaLabel="Held by filter"
              className="h-9 w-full"
              value={step ?? undefined}
              allLabel="Any step"
              options={READINESS_STEPS}
              onChange={(next) => void setFilters({ step: next ?? null, page: 1 })}
              searchPlaceholder="Search steps"
            />
          </ListFilter>
          <ListFilter label="State">
            <Select
              value={status}
              onValueChange={(next) => void setFilters({ status: next as StatusFilter, page: 1 })}
            >
              <SelectTrigger aria-label="State filter" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </ListFilter>
          <ListFilter label="Manufacturer">
            <SearchSelect
              ariaLabel="Manufacturer filter"
              className="h-9 w-full"
              value={manufacturer ?? undefined}
              allLabel="All manufacturers"
              options={(manufacturers.data ?? [])
                .filter((row) => row.waiting > 0)
                .map((row) => ({ value: row.manufacturer.uuid, label: row.manufacturer.name }))}
              onChange={(next) => void setFilters({ manufacturer: next ?? null, page: 1 })}
              searchPlaceholder="Search manufacturers"
            />
          </ListFilter>
        </ListFilters>
      )}

      {loading ? (
        <TableSkeleton />
      ) : view === 'products' ? (
        products.isError ? (
          <p className="py-12 text-center text-sm text-destructive">Could not load products.</p>
        ) : !products.data?.items?.length ? (
          <p className="py-12 text-center text-muted-foreground">
            {active || search ? 'No products match' : 'Every product is on the store'}
          </p>
        ) : (
          <ProductsTable rows={products.data.items} />
        )
      ) : manufacturers.isError ? (
        <p className="py-12 text-center text-sm text-destructive">Could not load manufacturers.</p>
      ) : !manufacturerRows.length ? (
        <p className="py-12 text-center text-muted-foreground">
          {search ? 'No manufacturers match' : 'Every product is on the store'}
        </p>
      ) : (
        <ManufacturersTable rows={manufacturerRows} onOpen={openProducts} />
      )}

      {view === 'products' && (products.data?.pages ?? 1) > 1 && (
        <div className="flex items-center justify-between px-4 py-3">
          <p className="text-sm text-muted-foreground">
            Page {products.data?.page ?? 1} of {products.data?.pages ?? 1} ({products.data?.total ?? 0} total)
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void setFilters({ page: Math.max(1, page - 1) })}
              disabled={page <= 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void setFilters({ page: page + 1 })}
              disabled={page >= (products.data?.pages ?? 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
